import { AlertTriangle, ArrowUp, CircleOff, Paperclip, Square, WifiOff } from "lucide-react";
import {
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type CompositionEvent,
  type FormEvent,
  type KeyboardEvent,
  type SyntheticEvent,
} from "react";

import { useSelector } from "react-redux";

import { Button } from "@/components/ui/button";
import { pluralize } from "@/lib/utils";

import { agentClient } from "../agentClient";
import { ComposerPrimitive, useAui, useAuiState, useComposerSync } from "../aui";
import { CaretOverlay } from "../carets";
import { useConnectionDown } from "../hooks/useConnectionDown";
import { useContextLabel } from "../hooks/useContextLabel";
import { FilingPopup } from "../choices/FilingPopup";
import type { FilingItem } from "../choices/wire";
import { useMentionCandidates, type MentionCandidate } from "../hooks/useMentionCandidates";
import type { WireCommand } from "../protocol/protocol";
import { getWorkspaceFileBytes, type WorkspaceFile } from "../sandbox/client/api";
import { STOPPABLE_RUN, type AgentState, type OutgoingFile } from "../store/agentSlice";
import { AttachmentChip } from "./AttachmentChip";
import { COMMAND_LIST_ID, CommandList, commandOptionId } from "./CommandList";
import { matchingCommands } from "./commands";
import { contentTypeFromName, FilePreviewDialog, type PreviewFile } from "./FilePreviewDialog";
import { FilesPanel, WorkspaceRoomNotice } from "./FilesPanel";
import { downloadBytes, mentionPreview, threadFileUrl } from "./files";
import { WorkspacePreviewDialog } from "./preview/WorkspacePreviewDialog";
import { findMention, matching, type MentionMatch } from "./mention";
import { MENTION_LIST_ID, MentionList, mentionOptionId } from "./MentionList";
import { MentionOverlay } from "./MentionOverlay";
import { ModelPill } from "./ModelPill";
import { useNotice } from "./notice";
import { uncounted, useCountDraftPdfs } from "./pageCounts";
import { TabDots } from "./TabDots";
import { useComposerMentions } from "./useComposerMentions";
import { useToggleWorkspaceFile, useWorkspaceFilesInMessage } from "./useAddFiles";
import { panelName } from "./useThreadWorkspace";
import { useThreadId } from "./useThreadId";

type WorkspaceCandidate = Extract<MentionCandidate, { kind: "workspace_file" }>;
/** A candidate the chat file preview shows (a workspace file has its own). */
type ChatCandidate = Exclude<MentionCandidate, WorkspaceCandidate>;

const ComposerChip = () => <AttachmentChip removable />;

/**
 * While the socket is down: what is sent now waits for it (its bubble says so too). A tab the
 * server refused promises no retry, since nothing makes one.
 */
const ConnectionNotice = memo(function ConnectionNotice() {
  const down = useConnectionDown();
  if (!down) return null;
  const Icon = down === "unavailable" ? CircleOff : WifiOff;
  return (
    <p role="status" className="flex items-start gap-1.5 px-3.5 pt-1 text-xs text-warning">
      <Icon className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
      {down === "unavailable"
        ? "The assistant isn't available right now."
        : "Not connected. Messages send once the connection is back."}
    </p>
  );
});

/** The General thread asks about any application; an application's, about it once named. */
function placeholder(general: boolean, label: string | null): string {
  if (general) return "Ask about any application…";
  return label ? `Ask about ${label}…` : "";
}

function ComposerAttachments() {
  const count = useAuiState((state) => state.composer.attachments.length);
  if (!count) return null;
  return (
    <div className="flex flex-wrap gap-1.5 px-3 pt-3">
      <ComposerPrimitive.Attachments components={{ Attachment: ComposerChip }} />
    </div>
  );
}

/**
 * Send, or Stop while the answer runs or waits on the sandbox and nothing is typed (typing sends to
 * the queue). `held`: the message waits for something (a mentioned document still linking). Send
 * also waits (disabled) while a draft PDF's pages are being counted (`counting`), as its pages'
 * default needs the count.
 */
function SendOrStop({ held, counting }: { held: boolean; counting: boolean }) {
  const threadId = useThreadId();
  const running = useAuiState((state) => state.thread.isRunning);
  const empty = useAuiState((state) => state.composer.isEmpty);
  // Also a run waiting on the sandbox, which is not running for the runtime (no Thinking).
  const stoppable = useSelector((state: { agent: AgentState }) => {
    const run = threadId == null ? null : state.agent.threads[threadId]?.run;
    return !!run && STOPPABLE_RUN.includes(run.status);
  });
  const stop = (onClick?: () => void) => (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="h-8 gap-1.5 max-sm:h-10"
      onClick={onClick}
    >
      <Square className="!size-3 fill-current" aria-hidden />
      Stop
    </Button>
  );
  if (running && empty) {
    return <ComposerPrimitive.Cancel asChild>{stop()}</ComposerPrimitive.Cancel>;
  }
  if (stoppable && empty && threadId != null) {
    return stop(() => void agentClient.cancel(threadId).catch(() => undefined));
  }
  return (
    <ComposerPrimitive.Send asChild disabled={held || counting}>
      <Button
        type="submit"
        size="icon"
        className="h-8 w-8 rounded-[9px] disabled:bg-muted disabled:text-muted-foreground disabled:opacity-100 max-sm:h-10 max-sm:w-10"
        aria-label="Send"
      >
        <ArrowUp />
      </Button>
    </ComposerPrimitive.Send>
  );
}

/**
 * The message being written: its files, the text (synced with the thread's draft in every tab),
 * @ to mention a file from this chat or the application (inline, and once in its files), / for a
 * command, the model, the other tabs, Send or Stop.
 * Use inside an AgentRuntime (and the NoticeProvider AgentChat gives).
 */
export function AgentComposer() {
  const aui = useAui();
  const threadId = useThreadId();
  const label = useContextLabel(threadId);
  // The General chat stays General's even with a deal linked to it (its focus, set by the agent).
  const general = useSelector(
    (state: { agent: AgentState }) =>
      threadId != null && state.agent.threads[threadId]?.profile === "chat"
  );
  const counting = useSelector(
    (state: { agent: AgentState }) =>
      threadId != null &&
      (state.agent.threads[threadId]?.composer.files ?? []).some((file) =>
        uncounted(state.agent, file)
      )
  );
  useCountDraftPdfs(threadId);
  const sync = useComposerSync(threadId);
  const { notice, setNotice } = useNotice();
  const candidates = useMentionCandidates(threadId);
  const input = useRef<HTMLTextAreaElement>(null);
  const mentions = useComposerMentions(threadId, input);
  const [match, setMatch] = useState<MentionMatch | null>(null);
  const [active, setActive] = useState(0);
  const [preview, setPreview] = useState<PreviewFile | null>(null);
  // A Files panel file's preview, from its workspace (the WU2 previewers).
  const [workspacePreview, setWorkspacePreview] = useState<WorkspaceCandidate | null>(null);
  const previewWorkspaceId = workspacePreview?.workspaceId ?? null;
  const toggleInMessage = useToggleWorkspaceFile();
  const inMessage = useWorkspaceFilesInMessage(threadId, previewWorkspaceId);
  const previewInMessage = workspacePreview ? inMessage.get(workspacePreview.file.path) : undefined;
  const readWorkspaceFile = useCallback(
    (file: WorkspaceFile) => getWorkspaceFileBytes(previewWorkspaceId ?? "", file.path),
    [previewWorkspaceId]
  );
  const downloadWorkspaceFile = useCallback(
    async (file: WorkspaceFile) =>
      downloadBytes(await readWorkspaceFile(file), panelName(file.path)),
    [readWorkspaceFile]
  );
  const focusInput = useCallback(() => input.current?.focus(), []);
  const commands = useSelector((state: { agent: AgentState }) => state.agent.commands);
  const text = useAuiState((state) => state.composer.text);
  const hasFiles = useAuiState((state) => state.composer.attachments.length > 0);
  // The text the command list was closed on (Escape, blur): it stays closed until the text changes.
  const [dismissed, setDismissed] = useState<string | null>(null);
  const [activeCommand, setActiveCommand] = useState(0);
  const commandItems = useMemo(
    () => (hasFiles || text === dismissed ? [] : matchingCommands(commands, text)),
    [commands, text, hasFiles, dismissed]
  );
  const commandsOpen = commandItems.length > 0;

  /** The eye of an @ row or a file to file: the same preview, or a notice when there is none. */
  const showPreview = useCallback(
    (candidate: ChatCandidate) => {
      if (threadId == null) return;
      void mentionPreview(threadId, candidate).then((result) => {
        if (!result) setNotice("That file can't be previewed.");
        else if ("file" in result) setPreview(result.file);
      });
    },
    [threadId, setNotice]
  );
  // A filing's files are on the thread: typed by the server when a message carries them.
  const previewFiling = useCallback(
    (item: FilingItem) => {
      const known = candidates.find(
        (candidate): candidate is ChatCandidate =>
          candidate.kind === "thread_file" && candidate.id === item.threadFileId
      );
      showPreview(
        known ?? {
          kind: "thread_file",
          id: item.threadFileId,
          name: item.name,
          contentType: contentTypeFromName(item.name) ?? "",
        }
      );
    },
    [candidates, showPreview]
  );

  const items = useMemo(
    // Each group (chat files, Files panel files, documents) keeps a share of the 8 rows.
    () => (match ? matching(candidates, match.query, 8, (candidate) => candidate.kind) : []),
    [candidates, match]
  );
  const open = items.length > 0;

  const track = (element: HTMLTextAreaElement) => {
    let next = findMention(element.value, element.selectionStart ?? element.value.length);
    // A finished mention is not a query: Enter still sends.
    if (next && mentions.isMention(element.value, next.start)) next = null;
    if (next?.query !== match?.query || next?.start !== match?.start) setActive(0);
    setMatch(next);
  };

  // A pick clears the last notice now, as typing would: its own edit, after, leaves the one its
  // add gives.
  const pick = (candidate: MentionCandidate) => {
    if (!match) return;
    setMatch(null);
    setNotice(null);
    mentions.pick(match, candidate);
  };

  const previewMention = useCallback(
    (file: OutgoingFile) => {
      if (threadId == null) return;
      void threadFileUrl(threadId, file.id).then((url) => {
        if (url) setPreview({ name: file.name, url, contentType: file.contentType });
        else setNotice("That file can't be previewed.");
      });
    },
    [threadId, setNotice]
  );

  const held = mentions.pending.length > 0;

  /** A command is sent as its own message; the server runs it (it never reaches the model). */
  const runCommand = (command: WireCommand) => {
    aui.composer.setText(command.name);
    aui.composer.send();
    setDismissed(null);
  };

  const onCommandKey = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActiveCommand((index) => (index + step + commandItems.length) % commandItems.length);
    } else if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      runCommand(commandItems[Math.min(activeCommand, commandItems.length - 1)]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setDismissed(text);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // An Enter that commits an IME composition is the input method's, not a send or a pick.
    // Safari sends that Enter with isComposing false, and keyCode 229.
    if (event.nativeEvent.isComposing || event.keyCode === 229) return;
    if (commandsOpen) return onCommandKey(event);
    // Enter waits with Send (see SendOrStop): a Ctrl/Meta chord (Shift+Ctrl+Enter is assistant-ui's
    // steer send) even with the @ list open, where only a plain Enter picks. A newline
    // (Shift+Enter) still goes in.
    if (
      counting &&
      event.key === "Enter" &&
      (event.ctrlKey || event.metaKey || (!open && !event.shiftKey))
    ) {
      event.preventDefault();
      return;
    }
    if (!open) {
      // Enter sends (Shift+Enter is a new line): not while a mentioned document is linking.
      const sends = event.key === "Enter" && (!event.shiftKey || event.ctrlKey || event.metaKey);
      if (held && sends) event.preventDefault();
      else mentions.onKeyDown(event);
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActive((index) => (index + step + items.length) % items.length);
    } else if ((event.key === "Enter" && !event.shiftKey) || event.key === "Tab") {
      event.preventDefault();
      pick(items[Math.min(active, items.length - 1)]);
    } else if (event.key === "Escape") {
      event.preventDefault();
      setMatch(null);
    }
  };

  return (
    <ComposerPrimitive.Root
      className="relative mx-auto w-full max-w-thread max-sm:-mx-3 max-sm:w-auto"
      onSubmit={(event: FormEvent) => {
        if (held) event.preventDefault(); // the Root then does not send
      }}
    >
      {commandsOpen && (
        <CommandList
          items={commandItems}
          active={Math.min(activeCommand, commandItems.length - 1)}
          onPick={runCommand}
          onActive={setActiveCommand}
        />
      )}
      {open && match && (
        <MentionList
          items={items}
          query={match.query}
          active={active}
          contextLabel={label}
          onPick={pick}
          onActive={setActive}
          onPreview={(candidate) => {
            if (candidate.kind === "workspace_file") setWorkspacePreview(candidate);
            else showPreview(candidate);
          }}
        />
      )}
      <FilePreviewDialog
        file={preview}
        onClose={() => {
          if (preview?.url.startsWith("blob:")) URL.revokeObjectURL(preview.url);
          setPreview(null);
        }}
      />
      <WorkspacePreviewDialog
        file={workspacePreview?.file ?? null}
        read={readWorkspaceFile}
        onDownload={downloadWorkspaceFile}
        inContext={previewInMessage !== undefined}
        onToggleContext={async (file) => {
          if (threadId == null || previewWorkspaceId == null) throw new Error("No workspace yet.");
          await toggleInMessage(threadId, previewWorkspaceId, file.path, previewInMessage ?? null);
        }}
        onClose={() => setWorkspacePreview(null)}
      />
      <FilesPanel threadId={threadId} />
      <FilingPopup
        threadId={threadId}
        className="mb-2"
        onSettled={focusInput}
        onPreview={previewFiling}
      />
      <div className="relative rounded-assistant border bg-card shadow-sm transition-shadow focus-within:ring-2 focus-within:ring-ring/60 max-sm:rounded-none max-sm:border-x-0 max-sm:border-b-0 max-sm:shadow-none max-sm:focus-within:ring-inset">
        <ComposerAttachments />
        <ComposerPrimitive.Input
          ref={input}
          rows={1}
          placeholder={placeholder(general, label)}
          className="block max-h-48 w-full resize-none overflow-y-auto bg-transparent px-3.5 pt-3 text-sm outline-none placeholder:text-muted-foreground"
          cancelOnEscape={false}
          unstable_focusOnThreadSwitched={false}
          aria-label="Message"
          role="combobox"
          aria-expanded={open || commandsOpen}
          aria-controls={commandsOpen ? COMMAND_LIST_ID : open ? MENTION_LIST_ID : undefined}
          aria-activedescendant={
            commandsOpen
              ? commandOptionId(Math.min(activeCommand, commandItems.length - 1))
              : open
                ? mentionOptionId(active)
                : undefined
          }
          aria-autocomplete="list"
          {...sync}
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
            const composing = (event.nativeEvent as InputEvent).isComposing === true;
            mentions.onChange(event.target, composing);
            sync.onChange(event);
            // No render of ours mid-composition: it would write the textarea's old text back
            // before the composer has the new one, which ends the IME session in Chromium.
            if (composing) return;
            if (!mentions.isPickEdit()) setNotice(null);
            setActiveCommand(0);
            track(event.target);
          }}
          onCompositionStart={mentions.onCompositionStart}
          onCompositionEnd={(event: CompositionEvent<HTMLTextAreaElement>) => {
            mentions.onCompositionEnd(event.currentTarget);
            setNotice(null);
            track(event.currentTarget);
          }}
          onSelect={(event: SyntheticEvent<HTMLTextAreaElement>) => {
            mentions.onSelect(event.currentTarget);
            // The caret moves as an IME composes: the @ query waits for its end, as onChange's does.
            if (!mentions.isComposing()) track(event.currentTarget);
          }}
          onBlur={() => {
            sync.onBlur();
            setMatch(null);
            setDismissed(text);
          }}
          onKeyDown={onKeyDown}
        />
        <MentionOverlay
          input={input}
          threadId={threadId}
          pending={mentions.pending}
          onPreview={previewMention}
        />
        <CaretOverlay input={input} threadId={threadId} />
        <span role="status" className="sr-only">
          {commandsOpen
            ? pluralize(commandItems.length, "command")
            : open
              ? pluralize(items.length, "file")
              : ""}
        </span>
        {notice && (
          <p role="alert" className="flex items-start gap-1.5 px-3.5 pt-1 text-xs text-destructive">
            <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
            {notice}
          </p>
        )}
        <WorkspaceRoomNotice threadId={threadId} />
        <ConnectionNotice />
        <div className="flex items-center gap-0.5 px-2 pb-2 pt-1">
          <ComposerPrimitive.AddAttachment asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground max-sm:h-10 max-sm:w-10"
              aria-label="Attach files"
            >
              <Paperclip />
            </Button>
          </ComposerPrimitive.AddAttachment>
          <ModelPill threadId={threadId} />
          <TabDots threadId={threadId} />
          <div className="ml-auto">
            <SendOrStop held={held} counting={counting} />
          </div>
        </div>
      </div>
    </ComposerPrimitive.Root>
  );
}
